import { createSlice } from "@reduxjs/toolkit";

import { getRandomId } from "../utils/id";

// Battle mode state. Persisted to localStorage ("owb.battle") via a store
// subscription so an accidental reload never loses a running game.
//
// Shape:
// {
//   active: null | Battle,
//   history: Battle[],          // finished battles, newest first
// }
//
// Battle:
// {
//   id, createdAt, finishedAt?, status: "setup" | "active" | "finished",
//   listId,                      // own list (optional — tracking can be skipped)
//   compositionRule,             // copied from the list at battle start
//   opponentName, opponentArmy,
//   opponentUnits: [{ id, name, points, strength }],  // manual/imported stubs
//   scenario, secondaries: [], scoringPack, firstTurn: "player" | "opponent",
//   currentTurn: 1,
//   sides: {
//     // Casualties tracked ON that side; VP for the OTHER side.
//     player:   Side,
//     opponent: Side,
//   },
// }
//
// Side:
// {
//   units: { [unitId]: { status, modelsLost, woundsLost, points? } },
//   banners: 0, scenarioPoints: 0, generalDead: false, bsbDead: false,
// }

const emptySide = () => ({
  units: {},
  banners: 0,
  scenarioPoints: 0,
  generalDead: false,
  bsbDead: false,
});

const initialState = {
  active: null,
  history: [],
};

export const battleSlice = createSlice({
  name: "battle",
  initialState,
  reducers: {
    setBattleState: (state, { payload }) => {
      return payload || initialState;
    },
    startBattle: (state, { payload }) => {
      const {
        listId,
        compositionRule,
        opponentName,
        opponentArmy,
        opponentUnits,
        scenario,
        secondaries,
        scoringPack,
        firstTurn,
      } = payload;

      state.active = {
        id: getRandomId(),
        createdAt: new Date().toISOString(),
        status: "active",
        listId: listId || null,
        compositionRule: compositionRule || null,
        opponentName: opponentName || "",
        opponentArmy: opponentArmy || "",
        opponentUnits: opponentUnits || [],
        scenario: scenario || null,
        secondaries: secondaries || [],
        scoringPack: scoringPack || null,
        firstTurn: firstTurn || "player",
        currentTurn: 1,
        sides: {
          player: emptySide(),
          opponent: emptySide(),
        },
      };
    },
    setTurn: (state, { payload }) => {
      if (!state.active) return;
      state.active.currentTurn = Math.max(1, payload);
    },
    setUnitState: (state, { payload }) => {
      // side: "player" | "opponent"
      const { side, unitId, status, modelsLost, woundsLost, points } = payload;
      if (!state.active) return;
      const units = state.active.sides[side].units;
      const current = units[unitId] || {
        status: "active",
        modelsLost: 0,
        woundsLost: 0,
      };

      units[unitId] = {
        ...current,
        ...(status !== undefined && { status }),
        ...(modelsLost !== undefined && { modelsLost: Math.max(0, modelsLost) }),
        ...(woundsLost !== undefined && { woundsLost: Math.max(0, woundsLost) }),
        ...(points !== undefined && { points }),
      };
    },
    setSideValue: (state, { payload }) => {
      // key: "banners" | "scenarioPoints" | "generalDead" | "bsbDead"
      const { side, key, value } = payload;
      if (!state.active) return;
      state.active.sides[side][key] = value;
    },
    finishBattle: (state, { payload }) => {
      if (!state.active) return;
      state.history.unshift({
        ...state.active,
        status: "finished",
        finishedAt: new Date().toISOString(),
        result: payload || null, // { playerVP, opponentVP, playerBP, opponentBP }
      });
      state.active = null;
    },
    abandonBattle: (state) => {
      state.active = null;
    },
    deleteBattleFromHistory: (state, { payload }) => {
      state.history = state.history.filter(({ id }) => id !== payload);
    },
  },
});

export const {
  setBattleState,
  startBattle,
  setTurn,
  setUnitState,
  setSideValue,
  finishBattle,
  abandonBattle,
  deleteBattleFromHistory,
} = battleSlice.actions;

export default battleSlice.reducer;
